import type { ThemePatch, ThemeVariant, StyleDraft } from '@ce/theme';
import type { NavTarget } from '../types';

/**
 * The app configuration — layout, navigation, style and features.
 *
 * This is deliberately a different document from the content bundle. Content is
 * what the event *has* (sessions, guests, prices); config is how the app
 * *presents* it (which tabs, which widgets on which page, what it looks like).
 * They change at different rates, are edited by different people, and are
 * served from different endpoints, so neither should be able to break the
 * other: a bad config falls back to a default layout over the same content, and
 * a content outage leaves the layout intact.
 *
 * Nothing in here is code. The admin produces this JSON; the apps interpret it
 * against a fixed widget catalogue. An unknown widget type is dropped rather
 * than executed, so a config can never make the app do something it was not
 * built to do.
 */

/** Bump on a breaking change. An app that sees a newer schema uses its default. */
export const CONFIG_SCHEMA = 1;

export type NavStyle = 'bottom' | 'floating' | 'top' | 'drawer';

export type BuiltinScreen =
  | 'schedule' | 'talent' | 'hall' | 'quests' | 'me' | 'events'
  | 'search' | 'journey' | 'shop' | 'tickets' | 'messages';

/** A tab points either at a built-in screen or at a page the admin composed. */
export type TabTarget =
  | { kind: 'screen'; screen: BuiltinScreen }
  | { kind: 'page'; pageId: string };

export interface TabDef {
  id: string;
  label: string;
  icon: string;
  target: TabTarget;
  enabled: boolean;
}

export interface HeaderConfig {
  search: boolean;
  inbox: boolean;
  cart: boolean;
  /** The list/grid toggle, which only ever appears on the schedule anyway. */
  grid: boolean;
}

export interface NavigationConfig {
  style: NavStyle;
  showLabels: boolean;
  tabs: TabDef[];
  header: HeaderConfig;
}

export type WidgetType =
  // Today
  | 'dayPicker' | 'upNext' | 'promoRotator' | 'quickLinks' | 'journeyCta'
  | 'callSheet' | 'featuredTalent' | 'specialEvents' | 'countdown'
  // Schedule
  | 'sessionList' | 'smartSuggestions'
  // Engage
  | 'poll' | 'liveFeedback' | 'quizCard'
  // Commerce
  | 'ticketCta' | 'merchStrip'
  // Content and layout
  | 'banner' | 'richText' | 'ctaButton' | 'spacer';

/** Who a widget is shown to. */
export type Audience = 'all' | 'badge' | 'nobadge';

export interface WidgetInstance {
  /** Stable and unique across the whole config; polls and feedback key off it. */
  id: string;
  type: WidgetType;
  props: Record<string, unknown>;
  hidden?: boolean;
  audience?: Audience;
  /** Day keys the widget is limited to. Omitted or empty means every day. */
  days?: string[];
}

export interface PageDef {
  id: string;
  title: string;
  /** Header eyebrow. Tokens: {dayLong} {day} {event} {dates}. */
  kicker: string;
  widgets: WidgetInstance[];
}

export interface FeatureFlags {
  journey: boolean;
  tickets: boolean;
  shop: boolean;
  themePicker: boolean;
  callSheet: boolean;
  askAssistant: boolean;
}

export interface EffectsConfig {
  /** Sheen sweeps and pulsing arcs. */
  ambient: boolean;
  /** Screen and list entrance animation. */
  entrance: boolean;
  /** The rotator's pan and text choreography. */
  rotatorMotion: boolean;
}

export interface AppConfig {
  schema: number;
  tenantId: string;
  /** Change on every publish; clients compare it to decide whether to swap. */
  revision: string;
  /** Brand, colours, radius — layered over the tenant's content-side theme. */
  theme: ThemePatch;
  /**
   * The admin's own picker state. The app ignores it; it exists so reopening the
   * editor restores the sliders instead of reverse-engineering them from
   * nine-step ramps.
   */
  style?: StyleDraft;
  /** Looks the attendee may switch between. Empty hides the picker. */
  themeVariants: ThemeVariant[];
  navigation: NavigationConfig;
  pages: Record<string, PageDef>;
  features: FeatureFlags;
  effects: EffectsConfig;
}

export type { NavTarget };

export interface ConfigIssue {
  severity: 'error' | 'warning';
  message: string;
  pageId?: string;
  widgetId?: string;
  /** Where to send the editor when the issue is clicked. */
  focus?: 'style' | 'navigation' | 'features' | 'page' | 'widget';
}
